<template>
  <div
    class="flex relative rounded-2xl border border-white focus:border-primary border-b-[#f0f1ff] transition-all duration-300 max-md:w-full"
    :class="{ '!border-primary border-b-primary': isFocus }"
  >
    <div class="relative w-1/2">
      <span class="absolute top-1/2 -translate-y-1/2 left-3 text-txsecondary text-sm">
        {{ fromLabel }}
      </span>
      <input
        :value="displayFromValue"
        @input="handleFromInput"
        @focus="isFocus = true"
        @blur="isFocus = false"
        type="text"
        :placeholder="fromPlaceholder"
        :style="{ paddingLeft: fromPadding || '2.2rem' }"
        class="w-full px-3 py-3.5 rounded-l-[15px] text-sm border border-b-2 border-r-0 border-white placeholder:text-[#959B9F] flex items-center leading-3"
      />
    </div>
    <div class="relative w-1/2">
      <span class="absolute top-1/2 -translate-y-1/2 left-3 text-txsecondary text-sm">
        {{ toLabel }}
      </span>
      <input
        :value="displayToValue"
        @input="handleToInput"
        @focus="isFocus = true"
        @blur="isFocus = false"
        type="text"
        :placeholder="toPlaceholder"
        :style="{ paddingLeft: toPadding || '2.2rem' }"
        class="w-full px-3 py-3.5 rounded-r-[15px] text-sm border border-b-2 border-white placeholder:text-[#959B9F] flex items-center leading-3"
      />
    </div>
    <el-slider
      v-model="sliderValue"
      class="absolute -bottom-[1px] px-3 runway-none"
      tooltip-class="slider-tooltip"
      :min="min"
      :max="max"
      :step="10"
      :class="{ 'default-value': modelValue.length === 0 }"
      @update:model-value="(value: number | number[]) => handleSliderChange(value as number)"
    />
  </div>
</template>

<script setup lang="ts">
  interface Props {
    min?: number;
    max?: number;
    fromLabel?: string;
    toLabel?: string;
    fromPlaceholder?: string;
    toPlaceholder?: string;
    fromPadding?: string;
    toPadding?: string;
    defaultValues?: number[] | (() => number[]);
  }

  const props = withDefaults(defineProps<Props>(), {
    min: 0,
    max: 100,
    fromLabel: "От",
    toLabel: "До",
    fromPlaceholder: "0",
    toPlaceholder: "0",
    defaultValues: () => [],
  });

  const emit = defineEmits(["update:modelValue"]);

  const modelValue = defineModel<number[]>({ required: true });

  const fromValue = ref<number>(modelValue.value.length > 0 ? modelValue.value[0] : 50);
  const toValue = ref<number>(modelValue.value.length > 0 ? modelValue.value[1] : 50);
  const sliderValue = ref<number>(fromValue.value);
  const isFocus = ref(false);

  const isEmpty = computed(() => {
    return modelValue.value.length === 0;
  });

  const displayFromValue = computed(() => {
    return isEmpty.value ? "—" : fromValue.value.toString() + "%";
  });

  const displayToValue = computed(() => {
    return isEmpty.value ? "—" : toValue.value.toString() + "%";
  });

  const handleFromInput = (e: Event) => {
    const target = e.target as HTMLInputElement;
    const value = target.value.replace(/[^\d]/g, "");

    if (value === "") {
      // Если поле пустое, устанавливаем дефолтные значения
      fromValue.value = 50;
      toValue.value = 50;
    } else {
      const numValue = parseInt(value);
      fromValue.value = Math.min(Math.max(numValue, props.min), props.max);
      toValue.value = props.max - fromValue.value;
    }

    sliderValue.value = fromValue.value;
    emit("update:modelValue", [fromValue.value, toValue.value]);
  };

  const handleToInput = (e: Event) => {
    const target = e.target as HTMLInputElement;
    const value = target.value.replace(/[^\d]/g, "");

    if (value === "") {
      fromValue.value = 50;
      toValue.value = 50;
    } else {
      const numValue = parseInt(value);
      toValue.value = Math.min(Math.max(numValue, props.min), props.max);
      fromValue.value = props.max - toValue.value;
    }

    sliderValue.value = fromValue.value;
    emit("update:modelValue", [fromValue.value, toValue.value]);
  };

  const handleSliderChange = (value: number) => {
    fromValue.value = value;
    toValue.value = props.max - value;
    emit("update:modelValue", [fromValue.value, toValue.value]);
  };

  watch(
    () => modelValue.value,
    (newValue) => {
      if (Array.isArray(newValue) && newValue.length > 0) {
        fromValue.value = newValue[0];
        toValue.value = newValue[1];
        sliderValue.value = fromValue.value;
      } else if (Array.isArray(newValue) && newValue.length === 0) {
        // Если массив пустой, устанавливаем значения 50/50 для слайдера, но не обновляем modelValue
        fromValue.value = 50;
        toValue.value = 50;
        sliderValue.value = fromValue.value;
      } else if (typeof newValue === "number") {
        fromValue.value = newValue;
        toValue.value = props.max - newValue;
        sliderValue.value = fromValue.value;
      }
    }
  );
</script>

<style lang="scss" scoped>
  input {
    color: theme("colors.txmain");
    font-size: 14px;
    height: auto;
    background-color: theme("colors.bgmain");
    border: 0 !important;

    &:focus {
      --tw-ring-offset-shadow: var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width)
        var(--tw-ring-offset-color);
      --tw-ring-shadow: var(--tw-ring-inset) 0 0 0 calc(0px + var(--tw-ring-offset-width))
        var(--tw-ring-color);
      box-shadow: var(--tw-ring-offset-shadow), var(--tw-ring-shadow), var(--tw-shadow, 0 0 #0000);
      --tw-ring-color: theme("colors.brfocus");
    }

    &::placeholder {
      color: theme("colors.txopacity");
    }
  }

  :deep(.el-slider) {
    .el-slider__runway {
      height: 2px;
      background-color: #f846c3;
    }

    .el-slider__bar {
      background-color: #3176ff;
    }

    &.default-value {
      .el-slider__runway {
        background-color: #f0f1ff;
      }

      .el-slider__bar {
        background-color: transparent;
      }
    }

    .el-slider__button {
      width: 14px;
      height: 14px;
      border: 2px solid white;
      background-color: theme("colors.primary");
      filter: drop-shadow(0px 1px 2.3px rgba(0, 0, 0, 0.12));
    }
  }
</style>

<style lang="scss">
  .slider-tooltip {
    background-color: white !important;
    color: theme("colors.txmain") !important;
    border: 0 !important;
    border-radius: 6px !important;
    box-shadow: 1px 2px 3px rgba(0, 0, 0, 0.12) !important;

    .el-popper__arrow {
      display: none;
    }
  }
</style>
